import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function AdminVerificationMonitor() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [thresholdDays, setThresholdDays] = useState(60);
  const [loading, setLoading] = useState(true);
  const [notifying, setNotifying] = useState(false);
  const [notifyResult, setNotifyResult] = useState(null);
  const isAdmin = user && (user.role === "admin" || user.is_owner);

  const load = async () => {
    if (!isAdmin) return;
    setLoading(true);
    setNotifyResult(null);
    try {
      const { data } = await api.get(`/admin/membership/verification-monitor?threshold_days=${thresholdDays}`);
      setItems(data.items || []);
      setCount(data.count || 0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isAdmin) load(); /* eslint-disable-next-line */ }, [thresholdDays, isAdmin]);

  const notifyAll = async () => {
    if (!window.confirm(`Send expiry notifications to ${count} painter${count === 1 ? "" : "s"}?`)) return;
    setNotifying(true);
    try {
      const { data } = await api.post(`/admin/membership/verification-monitor/notify?threshold_days=${thresholdDays}`);
      setNotifyResult(data);
    } finally {
      setNotifying(false);
    }
  };

  if (user === null) return <div className="p-10 text-center text-sm text-brand-muted">Loading…</div>;
  if (!isAdmin) {
    return (
      <div data-testid="admin-access-denied" className="max-w-3xl mx-auto px-6 py-20 text-center">
        <h2 className="font-serif text-3xl mb-3">Admin access required</h2>
        <a href="/projects" className="inline-block text-sm border border-brand-teal px-5 py-2 text-brand-teal hover:bg-brand-teal hover:text-white">Back to your projects</a>
      </div>
    );
  }

  const stateBadge = {
    expired: "bg-red-100 text-red-800",
    critical: "bg-red-50 text-red-700",
    warning: "bg-amber-100 text-amber-800",
    notice: "bg-brand-cream text-brand-navy",
  };

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="verification-monitor-page">
      <div className="mb-4">
        <Link to="/admin/membership-applications" className="text-xs text-brand-teal hover:underline">← Back to applications</Link>
      </div>
      <header className="mb-4">
        <div className="text-xs uppercase tracking-widest text-brand-teal font-semibold">Admin · ColourMatch Pro</div>
        <h1 className="text-2xl font-bold text-brand-navy">Verification monitor</h1>
        <p className="text-sm text-gray-600">Painter licences and insurance certificates expiring soon. Phase 2 policy: notify only, never auto-suspend.</p>
      </header>

      <div className="flex flex-wrap gap-3 items-end mb-4 p-3 bg-brand-cream border border-brand-border rounded-lg">
        <label className="text-xs">
          <div className="font-semibold text-brand-navy mb-1">Threshold (days)</div>
          <select value={thresholdDays} onChange={(e) => setThresholdDays(parseInt(e.target.value, 10))} className="border border-brand-border rounded px-2 py-1.5" data-testid="threshold-select">
            {[7, 14, 30, 60, 90, 180, 365].map((d) => <option key={d} value={d}>Within {d} days</option>)}
          </select>
        </label>
        <div className="ml-auto flex items-center gap-3">
          <span className="text-xs text-gray-600" data-testid="verification-count">
            {loading ? "Loading…" : `${count} document${count === 1 ? "" : "s"} within window`}
          </span>
          <button
            onClick={notifyAll}
            disabled={notifying || count === 0}
            className="text-xs bg-brand-teal text-white px-3 py-2 rounded-full font-semibold hover:bg-brand-teal/90 disabled:opacity-40"
            data-testid="notify-all-btn"
          >
            {notifying ? "Sending…" : `Notify all ${count > 0 ? `(${count})` : ""}`}
          </button>
        </div>
      </div>

      {notifyResult && (
        <div className="mb-3 p-3 rounded-lg bg-brand-success/10 border border-brand-success/40 text-sm text-brand-success" data-testid="notify-result">
          Sent {notifyResult.sent} of {notifyResult.found} notifications (test applications are always skipped).
        </div>
      )}

      <div className="border border-brand-border rounded-xl overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-brand-cream text-xs uppercase tracking-widest text-brand-navy">
            <tr>
              <th className="text-left px-3 py-2">Painter</th>
              <th className="text-left px-3 py-2 hidden sm:table-cell">Document</th>
              <th className="text-left px-3 py-2">Expiry</th>
              <th className="text-right px-3 py-2">Days</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody data-testid="monitor-tbody">
            {items.length === 0 && !loading && (
              <tr><td colSpan="6" className="p-8 text-center text-gray-500 italic" data-testid="monitor-empty">All member documents are current. Nothing to worry about.</td></tr>
            )}
            {items.map((it, i) => (
              <tr key={`${it.application_id}-${it.doc_id}`} className="border-t border-brand-border" data-testid={`monitor-row-${i}`}>
                <td className="px-3 py-2">
                  <div className="font-semibold text-brand-navy">{it.painter_name || it.business_name}</div>
                  <div className="text-xs text-gray-500">{it.painter_email}</div>
                </td>
                <td className="px-3 py-2 hidden sm:table-cell">
                  <div className="capitalize text-brand-navy">{it.kind}</div>
                  <div className="text-xs text-gray-500">#{it.doc_number || "—"}</div>
                </td>
                <td className="px-3 py-2 text-xs">{it.expiry}</td>
                <td className="px-3 py-2 text-right text-sm font-semibold">{it.days_until}</td>
                <td className="px-3 py-2">
                  <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${stateBadge[it.state] || "bg-gray-100 text-gray-700"}`}>{it.state}</span>
                </td>
                <td className="px-3 py-2 text-right">
                  <Link to={`/admin/membership-applications/${it.application_id}`} className="text-brand-teal hover:underline font-semibold text-xs">Open →</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
